import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
} from '@coreui/react';
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaFileAlt,
  FaInfoCircle,
} from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { showError } from 'src/utils/swalError';
import { GetSWAWorkItemList, UpdateWorkItemListToCompute, DownloadReportWeekly } from 'src/request/Projects';
import useLoading from 'src/utils/useLoading';
import useCustomPagination from 'src/utils/useCustomPagination';
import { DateRangePickerInput, formatDateString } from 'src/reusable';
import { debounce } from 'lodash';

const DownloadSwa = ({ visible, onClose, projectId, id }) => {
  const isShown = visible || false;
  const routeParams = useParams();
  const activeProjectId = projectId || id || routeParams?.id;

  const scrollContainerRef = useRef(null);
  const { startLoading, stopLoading, isLoading, InlineLoading } = useLoading();
  const { currentPage, pageSize, totalPages, updatePagination } = useCustomPagination();

  const [dataList, setDataList] = useState([]);
  const [selectedItemKeys, setSelectedItemKeys] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [isNext, setIsNext] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchWorkItems = useCallback(
    async (page = 1, append = false) => {
      if (!activeProjectId) return;
      if (append) {
        setIsLoadingMore(true);
      } else {
        startLoading();
      }

      try {
        const res = await GetSWAWorkItemList({
          project_id: activeProjectId,
          page: page,
          per_page: pageSize || 10,
        });

        if (res?.status === 200 || res?.data?.status === 200) {
          const responseData = res?.data || res;
          const items = responseData?.data_list || responseData?.data || [];

          if (append) {
            setDataList((prevItems) => {
              const itemMap = new Map();
              (prevItems ?? []).forEach((item) => {
                if (item.psow_id) itemMap.set(item.psow_id, item);
              });
              (items ?? []).forEach((item) => {
                if (item.psow_id) itemMap.set(item.psow_id, item);
              });
              return Array.from(itemMap.values());
            });

            const newEnabledKeys = items
              .filter(
                (item) =>
                  item.is_excluded === false ||
                  item.is_excluded === 0 ||
                  String(item.is_excluded).toLowerCase() === 'false'
              )
              .map((item) => item.psow_id);

            setSelectedItemKeys((prev) => Array.from(new Set([...prev, ...newEnabledKeys])));
          } else {
            setDataList(items);

            const enabledKeys = items
              .filter(
                (item) =>
                  item.is_excluded === false ||
                  item.is_excluded === 0 ||
                  String(item.is_excluded).toLowerCase() === 'false'
              )
              .map((item) => item.psow_id);
            setSelectedItemKeys(enabledKeys);

            setCollapsedGroups(new Set(items.map((item) => item.scope_of_work)));
          }

          const totalPages_ = responseData?.total_pages ?? 1;
          if (totalPages !== totalPages_) {
            updatePagination({ newTotalPages: totalPages_ });
          }
        }
      } catch (error) {
        console.error('Error fetching SWA work items:', error);
      } finally {
        if (append) {
          setIsLoadingMore(false);
        } else {
          stopLoading();
        }
      }
    },
    [activeProjectId, pageSize, totalPages, updatePagination, startLoading, stopLoading]
  );

  useEffect(() => {
    if (visible) {
      updatePagination({ newCurrentPage: 1 });
      fetchWorkItems(1, false);
    }
  }, [visible]);

  const handleScroll = debounce(() => {
    if (isLoadingMore || isLoading || currentPage >= totalPages) return;

    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;

      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        const nextPage = currentPage + 1;
        updatePagination({ newCurrentPage: nextPage });
        fetchWorkItems(nextPage, true);
      }
    }
  }, 200);

  const groupedItems = useMemo(() => {
    const groups = {};
    dataList.forEach((item) => {
      const groupName = item.scope_of_work || 'General';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(item);
    });
    return groups;
  }, [dataList]);

  const handleToggleItem = (key) => {
    setSelectedItemKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleToggleGroup = (groupItems) => {
    const groupKeys = groupItems.map((item) => item.psow_id);
    const groupSelectedCount = groupItems.filter((item) =>
      selectedItemKeys.includes(item.psow_id)
    ).length;
    const isAllGroupSelected = groupSelectedCount === groupItems.length;

    if (isAllGroupSelected) {
      setSelectedItemKeys((prev) => prev.filter((key) => !groupKeys.includes(key)));
    } else {
      setSelectedItemKeys((prev) => Array.from(new Set([...prev, ...groupKeys])));
    }
  };

  const toggleGroupCollapse = (groupName) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    return dataList.some((item) => {
      const isCurrentlySelected = selectedItemKeys.includes(item.psow_id);
      const isInitiallySelected =
        item.is_excluded === false ||
        item.is_excluded === 0 ||
        String(item.is_excluded).toLowerCase() === 'false';
      return isCurrentlySelected !== isInitiallySelected;
    });
  }, [dataList, selectedItemKeys]);

  const handleSaveWorkItems = async () => {
    if (!activeProjectId) return false;
    setIsSaving(true);
    try {
      const payload = {
        project_id: Number(activeProjectId),
        swa_settings: dataList.map((item) => ({
          psow_id: item.psow_id,
          is_excluded: selectedItemKeys.includes(item.psow_id) ? 0 : 1,
        })),
      };
      const res = await UpdateWorkItemListToCompute(payload);
      if (res?.status === 200 || res?.data?.status === 200) {
        setDataList((prev) =>
          prev.map((item) => ({
            ...item,
            is_excluded: selectedItemKeys.includes(item.psow_id) ? 0 : 1,
          }))
        );
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Successfully updated SWA settings.',
          timer: 2000,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating SWA work item settings:', error);
      showError(error?.response?.data?.message || 'Failed to update work item settings');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (hasChanges) {
      const success = await handleSaveWorkItems();
      if (success) {
        setIsNext(true);
      }
    } else {
      setIsNext(true);
    }
  };

  const handleBack = () => {
    setIsNext(false);
  };

  const handleClose = () => {
    setIsNext(false);
    if (onClose) onClose();
  };

  const WorkItemSelection = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-aimhi-info/10 px-4 py-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">
            <FaInfoCircle size={16} className="text-aimhi-info" />
          </div>

          <p className="text-sm leading-5 text-aimhi-textlight-neutral">
            <span className="font-semibold">Info:</span>{' '}
            All work items are selected by default and included in the SWA Report
            computation. You can exclude any work items that should not be included
            before downloading the report.
          </p>
        </div>

        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="max-h-[600px] overflow-y-auto divide-y divide-gray-200 border border-gray-200 rounded-lg"
        >
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <InlineLoading />
            </div>
          ) : Object.keys(groupedItems).length === 0 ? (
            <div className="p-12 text-center bg-white">
              <FaFileAlt className="mx-auto h-8 w-8 text-gray-300 mb-2" />
              <p className="text-sm font-medium text-gray-600">No work items found</p>
              <p className="text-xs text-gray-400 mt-1">There are no work items available for this report.</p>
            </div>
          ) : (
            Object.entries(groupedItems).map(([groupName, items]) => {
              const isCollapsed = collapsedGroups.has(groupName);
              const groupSelectedCount = items.filter((item) =>
                selectedItemKeys.includes(item.psow_id)
              ).length;
              const isAllGroupSelected = groupSelectedCount === items.length;
              const isSomeGroupSelected = groupSelectedCount > 0 && !isAllGroupSelected;

              return (
                <div key={groupName} className="bg-white">
                  <div className="bg-gray-50/80 px-6 py-3.5 flex items-center justify-between border-y border-gray-200/80 select-none">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id={`group-checkbox-${groupName}`}
                        checked={isAllGroupSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = isSomeGroupSelected;
                        }}
                        onChange={() => handleToggleGroup(items)}
                        className="h-4 w-4 rounded border-gray-300 text-[#3B74BE] focus:ring-[#3B74BE] cursor-pointer accent-[#3B74BE]"
                      />

                      <div className="flex items-center gap-2">
                        <span
                          onClick={() => handleToggleGroup(items)}
                          className="text-sm font-semibold text-gray-800 cursor-pointer hover:text-[#3B74BE] transition-colors"
                        >
                          {groupName}
                        </span>
                      </div>

                      <span className="text-xs text-gray-500 font-medium">
                        ({groupSelectedCount}/{items.length} items)
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleGroupCollapse(groupName)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded transition cursor-pointer"
                      aria-label={`Toggle ${groupName} section`}
                    >
                      {isCollapsed ? <FaChevronDown className="text-xs" /> : <FaChevronUp className="text-xs" />}
                    </button>
                  </div>

                  {!isCollapsed && (
                    <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {items.map((item) => {
                        const isChecked = selectedItemKeys.includes(item.psow_id);

                        return (
                          <div
                            key={item.psow_id}
                            onClick={() => handleToggleItem(item.psow_id)}
                            className={`flex items-center justify-between p-3.5 rounded-lg border transition-all cursor-pointer ${isChecked
                              ? 'border-[#3B74BE]/30 bg-blue-50/30 text-gray-900'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50/60'
                              }`}
                          >
                            <div className="flex items-center gap-3 min-w-0 pr-2">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => { }}
                                className="h-4 w-4 rounded border-gray-300 text-[#3B74BE] focus:ring-[#3B74BE] cursor-pointer accent-[#3B74BE] shrink-0"
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="text-xs font-medium " title={item.work_item}>
                                  {item.work_item}
                                </span>
                              </div>
                            </div>

                            {isChecked ? (
                              <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 border border-emerald-200 shrink-0 gap-1">
                                <FaCheckCircle className="text-[10px]" />
                                Included
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500 border border-gray-200 shrink-0">
                                Excluded
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {isLoadingMore && (
            <div className="p-4 flex items-center justify-center gap-2 bg-gray-50 text-xs text-gray-500">
              <InlineLoading />
            </div>
          )}
        </div>
      </div>
    );
  };

  const DataRangeSWA = () => {
    const [date, setDate] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);

    const getDefaultDates = () => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const sunday = new Date(today);
      sunday.setDate(today.getDate() - dayOfWeek);
      const saturday = new Date(sunday);
      saturday.setDate(sunday.getDate() + 6);
      return {
        start: formatDateString(sunday),
        end: formatDateString(saturday),
      };
    };

    const handleDownload = async () => {
      if (!activeProjectId) return;

      const defaultRange = getDefaultDates();

      const getDateRange = () => {
        if (typeof date === 'string') {
          const [startDate, endDate] = date.split(' - ').map((value) => value.trim());

          if (startDate && endDate) {
            return { startDate, endDate };
          }
        }

        if (date && typeof date === 'object') {
          const {
            startStr,
            endStr,
            formattedStartDate,
            formattedEndDate,
            value,
          } = date;

          if (startStr && endStr) {
            return {
              startDate: startStr,
              endDate: endStr,
            };
          }

          if (formattedStartDate && formattedEndDate) {
            return {
              startDate: formattedStartDate,
              endDate: formattedEndDate,
            };
          }

          if (typeof value === 'string') {
            const [startDate, endDate] = value
              .split(' - ')
              .map((value) => value.trim());

            if (startDate && endDate) {
              return { startDate, endDate };
            }
          }
        }

        return {
          startDate: defaultRange.start,
          endDate: defaultRange.end,
        };
      };

      const { startDate, endDate } = getDateRange();

      setIsDownloading(true);

      try {
        await DownloadReportWeekly(
          activeProjectId,
          startDate,
          endDate,
          4
        );

      } catch (error) {
        console.error('Error downloading SWA report:', error);

        if (error?.response?.status === 403) {
          showError(
            error?.response?.data?.response ||
            error?.response?.data?.message,
            'Permission Denied'
          );
        } else {
          showError(
            error?.response?.data?.message ||
            'Failed to download SWA report'
          );
        }
      } finally {
        setIsDownloading(false);
      }
    };

    return (
      <div className="space-y-4 py-2">
        <div className="flex items-start gap-3 rounded-lg border border-blue-200/80 bg-blue-50/60 px-4 py-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-aimhi-info shadow-xs border border-blue-100">
            <FaInfoCircle size={15} />
          </div>

          <p className="text-sm leading-5 text-gray-700 m-0 pt-0.5">
            <span className="font-semibold text-gray-900">Info:</span>{' '}
            All POW Amounts must be added before the SWA Report can be downloaded.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
          <div>
            <h6 className="mb-1 text-sm font-semibold text-gray-800">
              Reporting Period
            </h6>
            <p className="text-xs text-gray-500 mb-0">
              Select the date range to be covered in the SWA Report.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <DateRangePickerInput
              value={typeof date === 'string' ? date : date?.value || ''}
              onChange={(val) => setDate(val)}
              defaultToWeekToday={true}
              maxDays={7}
            />

            <CButton
              color="aimhi-textlight-neutral"
              disabled={isDownloading}
              onClick={handleDownload}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md shadow-xs hover:shadow transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
              <span>Download Report</span>
            </CButton>
          </div>
        </div>
      </div>
    );
  };

  return (
    <CModal size="xl" show={isShown} visible={isShown} onClose={handleClose}>
      <CModalHeader closeButton>
        <CModalTitle className="text-base font-semibold text-[#23303D]">
          Download SWA Report
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {!isNext ? <WorkItemSelection /> : <DataRangeSWA />}
      </CModalBody>
      <CModalFooter>
        {isNext && (
          <CButton color="aimhi-textlight-neutral" onClick={handleBack}>
            Back
          </CButton>
        )}
        {!isNext && (
          <CButton
            color="aimhi-textlight-neutral"
            disabled={isSaving || isLoading}
            onClick={handlePrimaryAction}
            className="flex items-center gap-1.5 cursor-pointer font-medium"
          >
            {isSaving ? 'Saving...' : hasChanges ? 'Save & Continue' : 'Continue'}
          </CButton>
        )}
      </CModalFooter>
    </CModal>
  );
};

export default DownloadSwa;
